import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Cookies from 'js-cookie';
import { useUser } from '@auth0/nextjs-auth0/client';
import { storeWrapper } from 'store';
import { useSelector, useDispatch } from 'react-redux';
import {
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  List,
  Paper,
  Typography,
} from '@mui/material';

import FullScreenDialog from 'components/FullScreenDialog';
import Layout from 'components/LayoutAbroad';
import LeadFormComponent from 'components/LeadFormComponent';
import SchoolFilters from 'components/SchoolsComponents/SchoolsFiltersComponent';
import SchoolItemComponent from 'components/SchoolsComponents/SchoolItemComponent';
import SchoolItemSkeletonComponent from 'components/SchoolsComponents/SchoolItemSkeletonComponent';
import SchoolsBreadcrumbComponent from 'components/SchoolsComponents/SchoolsBreadcrumbComponent';
import ScheduleAbroadMeetingComponent from 'components/SchoolsComponents/ScheduleAbroadMeetingComponent';

import {
  fetchSchoolDataFilter,
  featchSchoolPaged,
} from 'services/schoolService';
import { fetchCountries } from 'services/countryService';

import { UpdateFilter } from 'store/actions/schoolActions';
import { OpenCloseDialogLead } from 'store/actions/schoolDetailActions';

import { getSchoolsHead, extractPathSegments } from 'utils/headUtils';
import { hasPermissionByRole } from 'utils/auth/authHelper';
import { abroadDateSanitization } from 'utils/schoolUtils';
import { shouldUnlockPrice } from 'utils/lead';

const sortButtons = (items) =>
  items.map((item) => {
    const isBlackNovember = item.name === 'Black november';

    return (
      <Button
        key={item.name}
        variant={isBlackNovember ? 'contained' : 'outlined'}
        onClick={item?.click}
        size="small"
        sx={
          isBlackNovember
            ? {
                backgroundColor: '#f5c32e',
                color: '#000',
                mr: 1,
                ':hover': { backgroundColor: '#c29a24' },
              }
            : { mr: 1 }
        }
      >
        {item.name}
      </Button>
    );
  });

export const getServerSideProps = storeWrapper.getServerSideProps(
  async ({ req, res, store, query }) => {
    query.startDate = abroadDateSanitization(query.startDate);
    const initialState = store.getState();

    UpdateFilter(store.dispatch, {
      ...initialState.schoolFilterReducer,
      ...query,
    });

    await fetchSchoolDataFilter(store.dispatch, query);
    const countries = await fetchCountries(store.dispatch);
    const isSalesExecutive = await hasPermissionByRole(req, res, 'expert');

    const segments = extractPathSegments(req.url);
    const country = countries?.filter((co) => co.slugName === segments[1])[0];
    const city = country
      ? country?.cities?.filter((ci) => ci?.slugName === segments[2])[0]
      : null;

    const countryName = country ? country.name : '';
    const cityName = city ? city.name : '';

    return { props: { countryName, cityName, isSalesExecutive } };
  }
);

Schools.propTypes = {
  cityName: PropTypes.string,
  countryName: PropTypes.string,
};

Schools.defaultProps = {
  cityName: '',
  countryName: '',
};

const PAGE_SIZE = 10;

export default function Schools({ cityName, countryName, isSalesExecutive }) {
  const router = useRouter();
  const head = getSchoolsHead(cityName, countryName);

  const { user } = useUser();
  const { lead } = useSelector((store) => store.studentReducer);

  const studentIdentifier = Cookies.get('st-identifier');

  const isLeadUnlocked = shouldUnlockPrice(lead, user);

  const dispatch = useDispatch();
  const filterReducer = useSelector((state) => state.schoolFilterReducer);
  const countriesReducer = useSelector((state) => state.countryReducer);
  const { openDialogLead } = useSelector((state) => state.schoolDetailReducer);

  const [city, setCity] = useState(cityName);
  const [country, setCountry] = useState(countryName);
  const [schools, setSchools] = useState([]);
  const [schoolsPage, setSchoolsPage] = useState([]);
  const [page, setPage] = useState(10);
  const [updating, setUpdating] = useState(filterReducer.updateFilter);
  const [closeWidgetSalesExecutive, setCloseWidgetSalesExecutive] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const filters = [
    {
      name: 'Menor preço',
      variant: 'outlined',
      click: () => lowestPrice(),
    },
    {
      name: 'Limpar filtros',
      variant: 'text',
      click: () => cleanFilters(),
    },
    {
      name: 'Business Pack',
      variant: 'text',
      click: promotionFilter,
    },
  ];

  function promotionFilter() {
    const schoolsOrder = schools.filter((school) =>
      ['A', 'B'].includes(school?.commissionCategory)
    );

    setSchools(schoolsOrder);
    startData(schoolsOrder);
  }

  const lowestPrice = useCallback(() => {
    const schoolsOrder = schools.sort((a, b) => a.price - b.price);

    setSchools(schoolsOrder);
    startData(schoolsOrder);
  }, [schools]);

  const cleanFilters = () => {
    setIsLoading(true);
    window.location.href = window.location.pathname;
  };

  const getSchools = async (filter) => {
    setUpdating(filter.updateFilter);
    setSearchDestinationOnFilter(filter);

    const response = await featchSchoolPaged(filter);

    if (response) {
      setSchools(response.results);
      startData(response.results);
      setPage(10);
    }

    setUpdating(false);
  };

  const setSearchDestinationOnFilter = (filter) => {
    const countrySelected = countriesReducer?.filter(
      (c) => c.id === parseInt(filter.countryId, 10)
    )[0];

    const citySelected = countrySelected
      ? countrySelected?.cities?.filter(
          (c) => c.id === parseInt(filter?.cityId, 10)
        )[0]
      : '';

    if (countrySelected) setCountry(countrySelected.name);
    else setCountry('');
    if (citySelected) setCity(citySelected.name);
    else setCity('');
  };

  const startData = (items) => {
    const res = [];

    for (let i = 0; i < items.length; i += PAGE_SIZE) {
      res.push(items.slice(i, i + PAGE_SIZE));
    }

    if (res.length > 0) setSchoolsPage([...res[0]]);
    else setSchoolsPage([]);
  };

  const nextPage = () => {
    const addPaginate = page + 10;
    const paginate =
      addPaginate < schools.length ? addPaginate : schools.length;
    const schoolsPaginated = schools?.filter(
      (s, i) => i >= page && i < paginate
    );

    if (schoolsPaginated.length) {
      setPage(paginate);
      setSchoolsPage([...schoolsPage, ...schoolsPaginated]);
    }
  };

  let dinamycFromHeader = ':';
  if (country) dinamycFromHeader = ` ${country}:`;
  if (city) dinamycFromHeader = ` ${city}:`;

  let searchHeader = `Intercâmbio${dinamycFromHeader} Procurando escolas...`;

  let schoolsContent = <SchoolItemSkeletonComponent repeat={4} />;

  if (!updating) {
    if (schoolsPage.length > 0) {
      schoolsContent = schoolsPage.map((school, index) => (
        <SchoolItemComponent
          key={school.id}
          school={school}
          isSalesExecutive={isSalesExecutive}
          isLeadUnlocked={isLeadUnlocked}
          lead={lead}
          schoolItemIndex={index}
        />
      ));

      searchHeader = `Intercâmbio${dinamycFromHeader} ${schools.length} escolas encontradas`;
    } else if (updating !== undefined) {
      schoolsContent = 'Não encontramos escolas para o filtro selecionado.';
      searchHeader = `${schools.length} escolas encontradas`;
    }
  }

  const selectedCountry =
    countriesReducer &&
    countriesReducer.length > 0 &&
    countriesReducer?.filter(
      (c) => c.id === parseInt(filterReducer.countryId, 10)
    )[0];

  const selectedCity =
    selectedCountry &&
    selectedCountry?.cities?.filter(
      (c) => c.id === parseInt(filterReducer?.cityId, 10)
    )[0];

  useEffect(() => {
    if (router.query?.lowestPrice === 'true' && schools.length > 0) {
      lowestPrice();
    }
  }, [lowestPrice, router.query?.lowestPrice, schools.length]);

  return (
    <Layout maxWidth="lg" showFaq>
      <Head>
        <title>{head.title}</title>
        <meta name="description" content={head.metaDescription} />
      </Head>

      <SchoolsBreadcrumbComponent
        country={selectedCountry}
        city={selectedCity}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <SchoolFilters getSchools={getSchools} />
        </Grid>

        <Grid item xs={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h6" component="h1">
                {searchHeader}
              </Typography>
            </Grid>

            {!updating && schoolsPage.length > 0 && (
              <Grid item xs={12}>
                {sortButtons(filters)}
              </Grid>
            )}

            <Grid item xs={12}>
              <List>{schoolsContent}</List>
            </Grid>

            {!updating &&
            schoolsPage.length > 0 &&
            schoolsPage.length < schools.length ? (
              <Grid item xs={12}>
                <Paper elevation={2}>
                  <Button fullWidth onClick={() => nextPage()}>
                    Ver mais
                  </Button>
                </Paper>
              </Grid>
            ) : null}
          </Grid>
        </Grid>

        <ScheduleAbroadMeetingComponent
          hasMeetingScheduled={lead?.hasMeetingScheduled}
          isOpenDialogLead={openDialogLead}
          isQualifiedLead={lead?.qualifiedLead}
          studentIdentifier={studentIdentifier}
        />
      </Grid>

      <FullScreenDialog
        disableEscapeKeyDown={closeWidgetSalesExecutive}
        disableOnClose={!closeWidgetSalesExecutive}
        divider={false}
        maxWidth="sm"
        open={openDialogLead}
        setOpen={(open) => OpenCloseDialogLead(dispatch, open)}
        showCloseButton={closeWidgetSalesExecutive}
        showTitle={false}
      >
        <LeadFormComponent
          id="schools-widget"
          course={[]}
          country={[]}
          onMessageSuccess={() => {
            setCloseWidgetSalesExecutive(true);
          }}
        />
      </FullScreenDialog>

      <Backdrop open={isLoading} sx={{ zIndex: 1201 }}>
        <CircularProgress />
      </Backdrop>

      <input
        id="hcountry"
        name="hcountry"
        type="hidden"
        value={selectedCountry && selectedCountry.slugName}
      />
      <input
        id="hcity"
        name="hcity"
        type="hidden"
        value={selectedCity && selectedCity.slugName}
      />
    </Layout>
  );
}
